import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, FileX, ArrowLeft, Plus, Building2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { DataTable } from '@/components/shared/DataTable/DataTable';
import { DataTablePagination } from '@/components/shared/DataTable/DataTablePagination';
import NoFound from '@/components/shared/NoFound';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import useLeases from '../hooks/useLeases';
import { getLeaseColumns } from './columns';
import { LeaseFilters } from '../types/leases.types';
import { LeaseWithRelations } from '../types/lease';
import { LeaseStatus } from '@/common/api/services/leases';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface LeasesTableProps {
    filters: LeaseFilters;
    onPageChange: (page: number) => void;
    propertyUUID?: string;
    hidePropertyColumn?: boolean;
}

const LeasesTable = ({ filters, onPageChange, propertyUUID, hidePropertyColumn = false }: LeasesTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getLeases, deleteLease, updateLeaseStatus } = useLeases();
    const { data, isLoading, isError } = getLeases(filters);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [leaseToDelete, setLeaseToDelete] = useState<string | null>(null);

    const handleViewLease = (leaseUUID: string) => {
        if (propertyUUID) {
            navigate(`/home/properties/${propertyUUID}/leases/${leaseUUID}`);
        } else {
            const lease = data?.content.find(lease => lease.leaseUUID === leaseUUID);
            if (lease?.propertyUUID) {
                navigate(`/home/properties/${lease.propertyUUID}/leases/${leaseUUID}`);
            } else {
                console.error(`Lease ${leaseUUID} does not have propertyUUID`);
            }
        }
    };

    const handleEditLease = (leaseUUID: string) => {
        if (propertyUUID) {
            navigate(`/home/properties/${propertyUUID}/leases/${leaseUUID}`);
        } else {
            const lease = data?.content.find(lease => lease.leaseUUID === leaseUUID);
            if (lease?.propertyUUID) {
                navigate(`/home/properties/${lease.propertyUUID}/leases/${leaseUUID}`);
            } else {
                console.error(`Lease ${leaseUUID} does not have propertyUUID`);
            }
        }
    };

    const handleAddLease = () => {
        if (propertyUUID) {
            navigate(`/home/properties/${propertyUUID}/leases/create`);
        } else {
            navigate('/home/leases/create');
        }
    };

    const handleOpenDeleteDialog = (leaseUUID: string) => {
        setLeaseToDelete(leaseUUID);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (leaseToDelete) {
            deleteLease(leaseToDelete);
            setIsDeleteDialogOpen(false);
            setLeaseToDelete(null);
        }
    };

    const handleStatusChange = (leaseUUID: string, status: LeaseStatus) => {
        updateLeaseStatus({ uuid: leaseUUID, status });
    };

    const columns = useMemo(() => {
        return getLeaseColumns({
            t,
            onView: handleViewLease,
            onEdit: handleEditLease,
            onDelete: handleOpenDeleteDialog,
            onStatusChange: handleStatusChange,
            hidePropertyColumn
        });
    }, [t, hidePropertyColumn]);

    if (isError) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <NoFound
                        title={t('leases.noData.error.title')}
                        description={t('leases.noData.error.description')}
                    />
                </CardContent>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card>
                <CardContent className="pt-6 space-y-4">
                    <Skeleton className="h-8 w-full mb-6" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                </CardContent>
            </Card>
        );
    }

    const leases = data?.content || [];
    const totalElements = data?.totalElements || 0;
    const totalPages = data?.totalPages || 0;
    const page = filters.page || 0;

    if (leases.length === 0) {
        return (
            <Card>
                <CardContent className="pt-6">
                    <NoFound
                        icon={Building2}
                        badgeIcon={FileX}
                        title={t('leases.noData.title')}
                        description={t('leases.noData.description')}
                    />
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardContent className="p-0">
                    <DataTable
                        columns={columns}
                        data={leases as LeaseWithRelations[]}
                    />

                    {totalPages > 1 && (
                        <div className="flex justify-center py-4 border-t">
                            <DataTablePagination
                                table={{
                                    getState: () => ({
                                        pagination: {
                                            pageIndex: page,
                                            pageSize: filters.size || 10
                                        }
                                    }),
                                    getCanPreviousPage: () => page > 0,
                                    getCanNextPage: () => page < (totalPages - 1),
                                    previousPage: () => onPageChange(page - 1),
                                    nextPage: () => onPageChange(page + 1),
                                    setPageIndex: (pageIndex: number) => onPageChange(pageIndex),
                                    setPageSize: () => { },
                                    getPageCount: () => totalPages
                                } as any}
                                totalElements={totalElements}
                                totalPages={totalPages}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('leases.delete.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('leases.delete.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            {t('common.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default LeasesTable; 